import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

const norm = (s) => String(s || "").trim();

export const useClientTags = ({
  search = "",
  limit = 200,
  page = 1,
  includeUsed = false,
} = {}) => {
  const queryClient = useQueryClient();
  const qSearch = norm(search);
  const qLimit = Math.min(500, Math.max(1, Number(limit || 200)));
  const qPage = Math.max(1, Number(page) || 1);
  const qIncludeUsed = includeUsed ? 1 : 0;

  const query = useQuery(
    ["client-tags", { search: qSearch, limit: qLimit, page: qPage, includeUsed: qIncludeUsed }],
    async () => {
      const qs = new URLSearchParams({
        search: qSearch,
        limit: String(qLimit),
        page: String(qPage),
        includeUsed: String(qIncludeUsed),
      });
      return fetchData(`/client-tags?${qs.toString()}`);
    },
    {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      select: (res) => {
        const raw = res?.data ?? res;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        const pagination =
          res?.pagination && typeof res.pagination === "object"
            ? res.pagination
            : {
                total: list.length,
                page: qPage,
                limit: qLimit,
                totalPages: list.length > 0 ? 1 : 0,
              };
        return { list, pagination };
      },
    }
  );

  const list = query.data?.list || [];
  const pagination = query.data?.pagination || {
    total: list.length,
    page: qPage,
    limit: qLimit,
    totalPages: list.length > 0 ? 1 : 0,
  };

  const createMutation = useMutation(
    (payload) => fetchData("/client-tags", "POST", payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("client-tags");
        queryClient.invalidateQueries("filter-options");
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, ...payload }) => fetchData(`/client-tags/${id}`, "PUT", payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("client-tags");
        queryClient.invalidateQueries("filter-options");
      },
    }
  );

  const deleteMutation = useMutation((id) => fetchData(`/client-tags/${id}`, "DELETE"), {
    onSuccess: () => {
      queryClient.invalidateQueries("client-tags");
      queryClient.invalidateQueries("filter-options");
    },
  });

  return {
    ...query,
    data: list,
    pagination,
    createMutation,
    updateMutation,
    deleteMutation,
  };
};
